import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

import React, { useState, useRef, useEffect } from 'react';
import { Box, Grid, Typography, Link, Chip, IconButton } from '@mui/material';
import { PictureAsPdf, Description, Preview } from '@mui/icons-material';
import { Page, Document } from 'react-pdf';

import { useWindowDimensions } from '../hooks';
import { DISABLE_SWIPE } from '../utilities/enums';
import { useOverflowObserver, getLocalData, dbKeyDMSFiles, isTrainingMode } from '../helpers';
import { Modal } from '../components';
import { useDataServiceContext } from '../hooks';

export const CrewBriefingAttachments = ({ flightId }) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [isSwipeDisabled, setIsSwipeDisabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pageLength, setPageLength] = useState([null]);
  const [isTextModal, setIsTextModal] = useState(false);
  const [sABDWABCContent, setSABDWABCContent] = useState('');

  const [files, setFiles] = useState([]);
  const { width: windowWidth } = useWindowDimensions();
  const { flightData } = useDataServiceContext();

  useOverflowObserver(containerRef, contentRef, setIsSwipeDisabled);

  useEffect(() => {
    async function fetchData() {
      const parsedDmsArray =
        (await getLocalData(dbKeyDMSFiles(flightId))) || [];
      setFiles(parsedDmsArray);
    }
    fetchData();
    return () => setFiles([]);
  }, [flightId]);

  useEffect(() => {
    if (Array.isArray(flightData?.dmsFiles)) {
      setFiles(flightData.dmsFiles);
    }
  }, [flightData?.dmsFiles, flightId]);

  const trainingMode = isTrainingMode();

  // Only show files with base64 in training mode
  const filteredFiles = trainingMode
    ? files.filter((file) => file?.base64)
    : files;

  function base64ToBlob(base64, type = 'application/octet-stream') {
    const binStr = atob(base64);
    const len = binStr.length;
    const arr = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      arr[i] = binStr.charCodeAt(i);
    }
    return new Blob([arr], { type: type });
  }

  const openBase64NewTab = (base64Pdf) => {
    const blob = base64ToBlob(base64Pdf, 'application/pdf');
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl);
  };

  const handleModal = (index) => {
    const selectedDmsFile = files[index];
    setSelectedFile(selectedDmsFile);
    setIsTextModal(false);
    setIsModalOpen(!isModalOpen);
  };

  const handleTextModal = (text) => {
    setSABDWABCContent(text);
    setIsTextModal(true);
    setIsModalOpen(true);
  };
  return (
    <>
      <Box className={'content-container'} style={{ paddingBottom: 0 }}>
        <Typography variant='h6' gutterBottom textAlign={'center'}>
          CREW BRIEFING ATTACHMENTS
        </Typography>
      </Box>
      <div
        ref={containerRef}
        className={`crew-msg-container ${isSwipeDisabled ? DISABLE_SWIPE : ''}`}
      >
        <div className={DISABLE_SWIPE}>
          <Modal isOpen={isModalOpen} handleClose={() => {
            setIsModalOpen(false);
            setIsTextModal(false);
            setSABDWABCContent('');
          }}>
            <Grid item>
              <>
                {isTextModal ? (
                  <div>
                    <pre style={{
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word',
                      maxWidth: windowWidth * 0.78,
                      fontFamily: 'monospace',
                      fontSize: '10px'
                    }}>
                      {sABDWABCContent}
                    </pre>
                  </div>
                ) : (
                  <div>
                    <Document
                      file={`data:application/pdf;base64,${selectedFile?.base64}`}
                      onLoadSuccess={
                        ({ numPages }) =>
                          setPageLength(new Array(numPages).fill(null)) // Add an array with page length
                      }
                    >
                      {pageLength.map((index) => {
                        return (
                          <Page
                            key={index}
                            pageNumber={1}
                            width={windowWidth * 0.78}
                          />
                        );
                      })}
                    </Document>
                    <p></p>
                  </div>
                )}
              </>
            </Grid>
          </Modal>
        </div>
        <div ref={contentRef} style={{ width: 'fit-content' }}>
          {filteredFiles.length === 0 && !flightData?.allData?.sABDWABC?.[0] && (
            <>
              <Typography variant='body1' gutterBottom>
                {trainingMode && files.length > 0
                  ? 'No attachments available in training mode.'
                  : 'There are no attachments.'}
              </Typography>
            </>
          )}
          {flightData?.allData?.sABDWABC?.[0] && (
            <Grid container direction='row' flexWrap={'nowrap'} style={{ marginBottom: '8px' }}>
              <IconButton onClick={() => handleTextModal(flightData.allData.sABDWABC[0])}>
                <Preview />
              </IconButton>
              <Link
                style={{ alignSelf: 'center' }}
                onClick={() => handleTextModal(flightData.allData.sABDWABC[0])}
              >
                <Chip
                  sx={{
                    '& .MuiChip-label': {
                      textDecoration: 'underline',
                    },
                  }}
                  icon={<Description />}
                  label="WAB Calculation Details"
                />
              </Link>
            </Grid>
          )}
          {filteredFiles.map((item, filteredIndex) => {
            const fileName = item?.fileName ?? 'File has no name';
            const base64 = item?.base64 ?? undefined;
            const fileType = item?.type?.toUpperCase() || '';

            const originalIndex = files.findIndex((file) => file === item);

            return (
              <Grid key={filteredIndex} container direction='row' flexWrap={'nowrap'}>
                {fileType == 'PDF' && (
                  <IconButton onClick={() => handleModal(originalIndex)}>
                    <Preview />
                  </IconButton>
                )}

                {fileType.toUpperCase() === 'PDF' && !base64 && (
                  <Typography
                    variant='body1'
                    gutterBottom
                    style={{ textDecoration: 'line-through' }}
                  >
                    {fileName}
                  </Typography>
                )}

                <Link
                  style={{ alignSelf: 'center' }}
                  onClick={() => {
                    if (fileType.toUpperCase() === 'PDF') {
                      if (item.base64) {
                        openBase64NewTab(item.base64);
                      }
                    } else {
                      handleModal(originalIndex);
                    }
                  }}
                  sx={{
                    pointerEvents: fileType.toUpperCase() === 'PDF' && !item.base64 ? 'none' : 'auto',
                    opacity: fileType.toUpperCase() === 'PDF' && !item.base64 ? 0.5 : 1,
                  }}
                >
                  <Chip
                    sx={{
                      '& .MuiChip-label': {
                        textDecoration: 'underline',
                      },
                    }}
                    icon={
                      fileType === 'PDF' ? (
                        <PictureAsPdf sx={{ '&&': { color: 'red' } }} />
                      ) : (
                        <Description />
                      )
                    }
                    label={fileName}
                  />
                </Link>
              </Grid>
            );
          })}
        </div>
      </div>
    </>
  );
};
